// src/theme/theme.js
//
// Runtime side of the KaiLux theme system. The colour VALUES live in
// theme.css (single source of truth); this file exposes them to inline styles
// as CSS-variable references, and handles choosing/persisting the active theme.
//
// Why CSS variables rather than a plain JS palette object: pages used to each
// declare a module-level `const T = {...}` of hex strings, which is evaluated
// once at import. A runtime light/dark switch on such an object would need a
// full page reload — and a reload mid-AFS can lose a user's in-progress work.
// With variables the browser re-resolves every colour instantly.
//
// Consequence for page code: a token is now the string 'var(--kl-or)', so the
// old trick of appending hex alpha (`${T.or}30`) no longer works. Use
// alpha(T.or, 0.19) instead.

export const THEMES = ['light', 'dark', 'legacy']

// Light ("Ink and Paper") is the default for everyone. 'dark' is the user-chosen
// alternative (ThemeToggle). 'legacy' is the pre-refresh look, kept only as a
// preview/safety net reachable with ?theme=legacy; it can be deleted once the new
// design has settled (see theme.css and the legacy-fidelity test).
export const DEFAULT_THEME = 'light'

export const STORAGE_KEY = 'kailux:theme'

// Browser-chrome colour per theme (must equal each theme's --kl-bg; enforced by
// theme.test.js). legacy keeps the original orange PWA chrome.
export const THEME_COLOR = { light: '#F6F5F2', dark: '#0F1013', legacy: '#F97316' }

const TOKEN_KEYS = [
  'bg', 's1', 's2', 's3', 'dm', 'bd', 'tx', 'lt', 'mt',
  'or', 'or2', 'ord', 'orb', 'pri', 'prg', 'onpri',
  'gn', 'gnd', 'rd', 'rdd', 'am', 'amd', 'yl', 'yld', 'bl', 'bld', 'pu', 'pud',
  'sh', 'shl', 'scr', 'scrh', 'catink',
]

// T.bg === 'var(--kl-bg)'. Same key names pages already used.
export const T = Object.freeze(
  Object.fromEntries(TOKEN_KEYS.map(k => [k, `var(--kl-${k})`]))
)

// Translucent version of a token: alpha(T.or, 0.2). Replaces the old
// `${T.or}33` hex-alpha concatenation, which cannot work with var().
export function alpha(token, a) {
  const pct = Math.round(Math.min(1, Math.max(0, a)) * 100)
  return `color-mix(in srgb, ${token} ${pct}%, transparent)`
}

// A SOLID tint of a colour over the card surface: tint(T.rd, 10) is 10% red mixed
// into --kl-s2. For floating banners and toasts that must not be see-through
// (alpha() is translucent, which lets page content show through a fixed bar).
export function tint(color, pct) {
  return `color-mix(in srgb, ${color} ${pct}%, var(--kl-s2))`
}

// Text colour for a CATEGORICAL hex colour (a bank category, a chart series): the
// colour itself in legacy/dark, but darkened toward the ink colour in the light
// theme so pale hues (yellow, pink, lime) still clear WCAG AA on white. Use for
// text only; fills, dots and borders can use the raw colour.
export function ink(color) {
  return `color-mix(in srgb, ${color} var(--kl-catink), var(--kl-tx))`
}

const isTheme = v => THEMES.includes(v)

// Pages for people OUTSIDE the firm — today the director sign-off portal at
// /signoff/:token — must never show the migration-period 'legacy' (dark) theme:
// directors only ever saw a light page, and they have no theme toggle. On these
// paths 'legacy' resolves to 'light'; an explicit light/dark choice is respected.
// index.html's pre-paint script mirrors this (a test keeps them in sync).
export const PORTAL_PATH = /^\/signoff\//
export const portalTheme = theme => (theme === 'legacy' ? 'light' : theme)

// Query param (?theme=light|dark|legacy) wins and is remembered; otherwise the
// stored choice; otherwise the default. Used for previewing a theme on a Vercel
// preview URL without affecting anyone else.
export function resolveTheme(search = '', storage = null, pathname = '') {
  const r = resolveRaw(search, storage)
  return PORTAL_PATH.test(pathname) ? { ...r, theme: portalTheme(r.theme) } : r
}

function resolveRaw(search, storage) {
  let fromQuery = null
  try { fromQuery = new URLSearchParams(search).get('theme') } catch { /* ignore */ }
  if (isTheme(fromQuery)) return { theme: fromQuery, persist: true }
  let stored = null
  try { stored = storage ? storage.getItem(STORAGE_KEY) : null } catch { /* ignore */ }
  if (isTheme(stored)) return { theme: stored, persist: false }
  return { theme: DEFAULT_THEME, persist: false }
}

const listeners = new Set()
let current = DEFAULT_THEME

export function getTheme() { return current }

export function applyTheme(theme, { persist = true } = {}) {
  if (!isTheme(theme)) return
  current = theme
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme)
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute('content', THEME_COLOR[theme])
  }
  if (persist) {
    try { localStorage.setItem(STORAGE_KEY, theme) } catch { /* private mode etc. */ }
  }
  listeners.forEach(fn => fn(theme))
}

export function subscribeTheme(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// Called once from main.jsx before first render. The inline <script> in
// index.html has already set data-theme before first paint; this syncs the
// module state, the theme-color meta, and persists a ?theme= preview choice.
export function initTheme() {
  const { theme, persist } = resolveTheme(
    typeof window !== 'undefined' ? window.location.search : '',
    typeof localStorage !== 'undefined' ? localStorage : null,
    typeof window !== 'undefined' ? window.location.pathname : '',
  )
  applyTheme(theme, { persist })
  return theme
}
